import dayjs from "dayjs"
import i18n from "i18n"
import { numberToCurrency } from "helpers/string"
import { parseDateOnly } from "helpers/time"
import { computeDiscountAmount } from "utils/discountUtils"
import { grossUpCardFee, roundToCents } from "utils/bookingCalculations"
import { summarizeAddOnFees } from "components/bookings/helpers"
import { enumerateDates } from "utils/dateSpan"
import { getPowerTranzCardFee } from "helpers/powertranz"

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"]

export const PROGRESSIVE_DIRECTION = { INCREASE: "increase", DECREASE: "decrease" }
export const PROGRESSIVE_UNIT = { PERCENT: "percent", FIXED: "fixed" }

/**
 * Progressive duration pricing adjustment for the nth day of a dynamic trip (1-based).
 *
 * Ported from the backend's source of truth, `TripBookings::ProgressiveDurationPricingCalculator`
 * (API/app/services/trip_bookings/progressive_duration_pricing_calculator.rb) — this must match it
 * exactly, not just be "close":
 *
 *   day <= threshold       -> full rate, no adjustment
 *   threshold < day <= cap -> ONE FLAT adjustment (progressive_pricing_value%, or a flat $ amount)
 *                             — NOT scaled by how far past the threshold the day is (day 4 and day
 *                             7 move by the same amount; there is no escalation)
 *   day > cap              -> reverts to the full rate; the ramp does not persist once capped
 *
 * Percent example: $100/day/guest, threshold=3, cap=7, value=10%, direction=decrease ->
 *   days 1-3: $100 each. Days 4-7: $90 each (flat 10% off). Day 8+: $100 again. Scales by
 *   guest count naturally since it is a percentage of each guest's own per-day rate.
 *
 * Fixed ($) example: threshold=2, cap=4, value=$10, direction=decrease, 2 guests ->
 *   days 1-2: full rate. Days 3-4: $10 off PER GUEST ($20 off the day's aggregate rate) —
 *   the $ amount applies per guest, matching how the percent unit already scales with guest
 *   count. Day 5+: full rate again.
 *
 * Lives here (rather than in multiDayPricing.js, its main caller) because multiDayPricing.js
 * already imports the adjustment primitives below from this file — defining it there and importing
 * it back here would create a cycle. multiDayPricing.js re-exports it for its own callers.
 *
 * @param {Number} guestCount - total guests on the booking. Only read for a "fixed" ($) unit
 *   ramp (the percent unit needs no guest count — see the class doc above). Defaults to 1 for
 *   callers that only need the (guest-count-independent) percent multiplier.
 * @returns {{ multiplier: Number, flatDelta: Number }} applied as base × multiplier + flatDelta.
 */
export const getProgressiveAdjustment = (trip, dayIndex, guestCount = 1) => {
  const none = { multiplier: 1, flatDelta: 0 }
  // Dynamic multi-day only — a FIXED multi-day trip can still sell an option at a "per_day"
  // rate_basis (summed by day count, no per-day-index ramp), so callers can't rely on
  // isPerDay/rate_basis alone to exclude it. Gated here too (not just at call sites, which
  // already check isDynamic before reaching this point) so no future caller can accidentally
  // apply this ramp to a fixed or single-day trip. Mirrors the backend's
  // ProgressiveDurationPricingCalculator#applicable?, which gates the same way.
  if (trip?.duration_type !== "multi_day" || trip?.duration_mode !== "dynamic") return none
  if (!trip?.progressive_pricing_enabled) return none

  const threshold = Number(trip.progressive_pricing_threshold) || 0
  const cap = Number(trip.progressive_pricing_cap) || 0
  const value = Number(trip.progressive_pricing_value) || 0
  if (!value) return none

  // Only days strictly after the threshold and at or before the cap are ramped — everything else
  // (at/before the threshold, or past the cap) pays the plain rate.
  if (dayIndex <= threshold || dayIndex > cap) return none

  const sign = trip.progressive_pricing_direction === PROGRESSIVE_DIRECTION.DECREASE ? -1 : 1

  if (trip.progressive_pricing_unit === PROGRESSIVE_UNIT.FIXED) {
    return { multiplier: 1, flatDelta: value * sign * Math.max(1, Number(guestCount) || 1) }
  }
  return { multiplier: Math.max(0, 1 + (value * sign) / 100), flatDelta: 0 }
}

const dayAdjustmentApplies = (adjustment, weekdayKey) => {
  const { applies_on, custom_days } = adjustment || {}
  if (applies_on === "weekend") return weekdayKey === "sat" || weekdayKey === "sun"
  if (applies_on === "weekdays") return weekdayKey !== "sat" && weekdayKey !== "sun"
  if (applies_on === "custom") return (custom_days || []).includes(weekdayKey)
  return false
}

// An adjustment scoped to specific ticket types (affects: "selected") only applies when pricing one
// of those types; without a ticket type (private bookings) it never applies.
const appliesToTicketType = (adjustment, ticketTypeId) =>
  adjustment.affects !== "selected" ||
  (ticketTypeId != null && (adjustment.selected_ticket_ids || []).map(String).includes(String(ticketTypeId)))

/**
 * Returns the day adjustments (from experience.day_adjustments) that apply to the trip's
 * start date, based on that date's weekday vs. each adjustment's applies_on/custom_days.
 * Adjustments scoped to specific ticket types (affects: "selected") are skipped unless
 * `ticketTypeId` is one of them — private bookings have no ticket-type selection.
 */
export const getApplicableDayAdjustments = (dayAdjustments, dateStr, ticketTypeId) => {
  if (!Array.isArray(dayAdjustments) || !dayAdjustments.length || !dateStr) return []
  const parsedDate = parseDateOnly(dateStr)
  if (!parsedDate) return []
  const weekdayKey = WEEKDAY_KEYS[parsedDate.getDay()]
  return dayAdjustments.filter((adjustment) => appliesToTicketType(adjustment, ticketTypeId) && dayAdjustmentApplies(adjustment, weekdayKey))
}

// Dates arrive as "YYYY-MM-DD" or a full ISO timestamp; only the calendar day matters here.
const toIsoDate = (value) => (value ? String(value).slice(0, 10) : "")
const toMonthDay = (isoDate) => Number(isoDate.slice(5, 7)) * 100 + Number(isoDate.slice(8, 10))

/**
 * Whether a booking date falls inside one adjustment range. With is_repeat_each_year the stored
 * year is ignored and only month/day is compared, so a range that wraps the year end
 * (e.g. Dec 20 – Jan 5) matches on both sides of the boundary. Otherwise the literal dates are
 * compared (ISO date strings sort lexicographically, so a plain string compare is correct).
 */
const dateInAdjustmentRange = (dateStr, range, isRepeatEachYear) => {
  const start = toIsoDate(range?.start)
  const end = toIsoDate(range?.end)
  if (!start || !end) return false
  if (!isRepeatEachYear) return dateStr >= start && dateStr <= end
  const [target, from, to] = [toMonthDay(dateStr), toMonthDay(start), toMonthDay(end)]
  return from <= to ? target >= from && target <= to : target >= from || target <= to
}

/**
 * Resolves the date ranges a seasonal adjustment covers, normalized to { start, end }:
 *  - default_schedule → the experience's own season ranges (trip.available_date_ranges)
 *  - custom_dates     → the adjustment's nested date_ranges
 *  - ticketed         → the flat start_date / end_date pair
 * "holidays" adjustments carry no dates the frontend can resolve, so they never match here —
 * the backend owns that calendar.
 */
const getSeasonalAdjustmentRanges = (adjustment, seasonRanges) => {
  if (adjustment.applies_on_schedule_type === "default_schedule") return seasonRanges
  if (adjustment.date_ranges?.length) {
    return adjustment.date_ranges.map((range) => ({ start: range.start_date, end: range.end_date }))
  }
  if (adjustment.start_date && adjustment.end_date) {
    return [{ start: adjustment.start_date, end: adjustment.end_date }]
  }
  return []
}

/**
 * Returns the seasonal adjustments (from experience.seasonal_adjustments) covering the trip's
 * start date. Adjustments scoped to specific ticket types (affects: "selected") are skipped
 * unless `ticketTypeId` is one of them — private bookings have no ticket-type selection.
 * @param {Array} seasonalAdjustments - experience.seasonal_adjustments
 * @param {String} dateStr - trip start date ("YYYY-MM-DD")
 * @param {Array} seasonRanges - experience.available_date_ranges ([{ start, end }]), used by
 *   adjustments that apply on the experience's default schedule.
 * @param {Number|String} [ticketTypeId] - the ticket type being priced (ticketed trips)
 */
export const getApplicableSeasonalAdjustments = (seasonalAdjustments, dateStr, seasonRanges, ticketTypeId) => {
  if (!Array.isArray(seasonalAdjustments) || !seasonalAdjustments.length || !dateStr) return []
  const bookingDate = toIsoDate(dateStr)
  return seasonalAdjustments.filter((adjustment) => {
    if (!appliesToTicketType(adjustment, ticketTypeId)) return false
    const isRepeatEachYear = adjustment.is_repeat_each_year ?? true
    return getSeasonalAdjustmentRanges(adjustment, seasonRanges ?? []).some((range) =>
      dateInAdjustmentRange(bookingDate, range, isRepeatEachYear)
    )
  })
}

/**
 * Layered pricing: every adjustment that applies to the date is computed off the BASE unit price
 * and the deltas are summed — a $50 base with a +20% weekend and a +20% seasonal adjustment is
 * $70, not $72. Fixed "decrease" adjustments can exceed the base, so the result is floored at 0.
 * @param {Number|String} price - base unit price
 * @param {Array} adjustments - applicable day + seasonal adjustments
 */
const getAdjustmentsDelta = (base, adjustments = []) =>
  adjustments.reduce((sum, adjustment) => {
    const rawAmount =
      adjustment.price_type === "percent"
        ? (base * (Number(adjustment.price_value) || 0)) / 100
        : Number(adjustment.price_value) || 0
    return sum + (adjustment.direction === "decrease" ? -rawAmount : rawAmount)
  }, 0)

export const applyPriceAdjustments = (price, adjustments = []) => {
  const base = Number.parseFloat(price ?? 0) || 0
  return Math.max(0, base + getAdjustmentsDelta(base, adjustments))
}

/**
 * True when the decreases stacked on a non-zero base price wipe it out entirely, so the displayed
 * $0.00 is the floor rather than the real arithmetic. Drives the explanatory tooltip on the
 * affected breakdown line — the price itself stays clamped either way.
 * @param {Number|String} price - base unit price
 * @param {Array} adjustments - applicable day + seasonal adjustments
 */
export const isPriceZeroedByAdjustments = (price, adjustments = []) => {
  const base = Number.parseFloat(price ?? 0) || 0
  if (base <= 0 || adjustments.length === 0) return false
  return base + getAdjustmentsDelta(base, adjustments) <= 0
}

/**
 * Computes the guest-visible portion of the platform tax based on platform_fee_setting.
 * guide_pays  → guest pays $0
 * guest_pays  → guest pays full amount
 * split+%     → guest pays (100 - guide_portion)% of the tax
 * split+fixed → guest pays max(0, tax - guide_portion)
 */
export const applyPlatformFeeSetting = (rawTaxAmount, platformFeeSetting) => {
  if (!platformFeeSetting) return rawTaxAmount
  const { fee_payer_mode, split_type, guide_portion } = platformFeeSetting
  if (fee_payer_mode === "guide_pays") return 0
  if (fee_payer_mode === "guest_pays") return rawTaxAmount
  if (fee_payer_mode === "split") {
    const guideShare = Number(guide_portion) || 0
    if (split_type === "fixed") return Math.max(0, rawTaxAmount - guideShare)
    // percentage
    return rawTaxAmount * (1 - guideShare / 100)
  }
  return rawTaxAmount
}

/**
 * Computes the GROSS guest subtotal (before discount, add-ons, and tax) from a saved booking's
 * payment_summary. The per-guest entries (adult/child/…) store `price` as the LINE total
 * (count × unit), so we sum those line totals. Used to seed bookingSelections.baseAmount in edit
 * mode — payment_summary.subtotal is net of the discount, so it can't be used as the discount base.
 * @param {Object} paymentSummary - booking.payment_summary
 */
export const computeGuestSubtotalFromPaymentSummary = (paymentSummary = {}) => {
  const {
    adult, child, infant, passenger,
    pricing_type, extra_adult, extra_child, extra_infant, extra_passenger, flat_rate
  } = paymentSummary ?? {}
  const lineTotal = (item) => (item?.count ? Number(item?.price) || 0 : 0)
  if (pricing_type === "charge_flat_rate") {
    return (
      (Number(flat_rate) || 0) +
      lineTotal(extra_adult) + lineTotal(extra_child) + lineTotal(extra_infant) + lineTotal(extra_passenger)
    )
  }
  return lineTotal(adult) + lineTotal(child) + lineTotal(infant) + lineTotal(passenger)
}

// payment_summary guest line -> the schedule rate it is charged at, per pricing type.
const SOLD_LINE_RATE_KEYS = {
  charge_per_person: { adult: "price_per_person", child: "price_per_child", infant: "price_per_infant", passenger: "price_per_passenger" },
  charge_flat_rate: {
    flat_rate: "flat_rate",
    extra_adult: "price_per_person",
    extra_child: "price_per_child",
    extra_infant: "price_per_infant",
    extra_passenger: "price_per_passenger"
  }
}

const GUEST_TOTAL_KEYS = ["adults", "children", "infants", "passengers"]

const guestTotals = (counts) => {
  const main = counts?.main || {}
  const extra = counts?.extra || {}
  return GUEST_TOTAL_KEYS.map((key) => (Number(main[key]) || 0) + (Number(extra[key]) || 0))
}

/**
 * Attaches to a saved booking's schedule the unit prices the booking was actually CHARGED, read
 * back from its payment_summary guest lines (which the backend reconciles to the stored subtotal).
 *
 * The backend keeps a booking's guest subtotal as stored unless its date, duration or guest counts
 * change — an add-on-only edit never re-prices the guests. Re-deriving them here from the schedule
 * and the day/seasonal rules instead only agrees while the stored price still matches those rules
 * on the booking's date, which a booking rescheduled without a re-price no longer does (booking
 * 1568238359: sold $1,100 on a weekend, moved into a +100% season, re-quoted as $2,000 on an
 * add-on edit). calculateBookingTotal uses these prices only while the date and guest counts are
 * still the saved ones; any change drops back to the schedule plus the rules.
 *
 * @param {Object} schedule - booking.trip_schedule
 * @param {Object} paymentSummary - booking.payment_summary
 * @param {string} date - booking.booked_date
 * @param {Object} counts - the seeded { main, extra } guest counts
 */
export const withSoldUnitPrices = (schedule, paymentSummary, date, counts) => {
  const rateKeys = SOLD_LINE_RATE_KEYS[paymentSummary?.pricing_type]
  if (!schedule || !rateKeys || !date) return schedule

  const soldRates = Object.entries(rateKeys).reduce((rates, [line, rateKey]) => {
    const item = paymentSummary[line]
    if (line === "flat_rate") {
      if (item != null) rates[rateKey] = Number(item) || 0
    } else if (item?.count > 0) {
      rates[rateKey] = (Number(item.price) || 0) / item.count
    }
    return rates
  }, {})

  return {
    ...schedule,
    sold_pricing: {
      date,
      guestTotals: guestTotals(counts),
      price: { ...schedule, ...soldRates, price_adjustments_applied: true }
    }
  }
}

// The saved booking's charged unit prices while nothing that re-prices its guests has changed;
// otherwise the schedule itself, for the day/seasonal rules to be folded in.
const resolveSchedulePrice = (price, date, counts) => {
  const sold = price?.sold_pricing
  if (!sold) return price || {}

  const unchanged = sold.date === date && guestTotals(counts).every((total, i) => total === sold.guestTotals[i])
  return unchanged ? sold.price : price
}

/**
 * Builds a price breakdown array (guest line items + grouped "Tax & Fees") from a saved
 * booking's payment_summary. Used to seed bookingSelections.breakdown in edit mode so the
 * "Updated Price Details" panel shows the full breakdown before the user changes anything
 * (the live recalc via calculateBookingTotal is skipped while there are no changes).
 *
 * The output format mirrors calculateBookingTotal's breakdown so PriceBreakdown renders it
 * identically. Discount and add-ons are intentionally excluded — PriceBreakdown renders those
 * as separate rows.
 * @param {Object} paymentSummary - booking.payment_summary
 */
export const buildBreakdownFromPaymentSummary = (paymentSummary = {}) => {
  const {
    tax_and_fee,
    adult,
    child,
    infant,
    passenger,
    pricing_type,
    extra_adult,
    extra_child,
    extra_infant,
    extra_passenger,
    flat_rate,
    custom_fees
  } = paymentSummary ?? {}

  const breakdown = []

  const addGuestLine = (label, item) => {
    if (item?.count && item?.price) {
      breakdown.push({ label: `${label} x ${item.count}`, amount: numberToCurrency(Number(item.price)) })
    }
  }

  if (pricing_type === "charge_flat_rate") {
    breakdown.push({ label: "Flat Rate", amount: numberToCurrency(Number(flat_rate ?? 0)) })
    addGuestLine("Extra Adult", extra_adult)
    addGuestLine("Extra Child", extra_child)
    addGuestLine("Extra Infant", extra_infant)
    addGuestLine("Extra Passenger", extra_passenger)
  } else {
    addGuestLine("Adult", adult)
    addGuestLine("Child", child)
    addGuestLine("Infant", infant)
    addGuestLine("Passenger", passenger)
  }

  // The card processing fee is shown on its own line below the Total, never inside Tax & Fees, so
  // it is dropped here even when the saved custom_fees carry it.
  const taxAndFeeItems = (custom_fees ?? []).filter((fee) => fee.title !== "Card Processing Fee")

  const feeChildren = taxAndFeeItems.map((fee) => ({
    label: fee.title,
    amount: numberToCurrency(Number(fee.price))
  }))
  // The platform fee (tax_and_fee) is already represented inside custom_fees as a "Platform Fee"
  // entry. Only add it separately for legacy bookings whose custom_fees omit it — otherwise it
  // renders twice.
  const hasPlatformFee = taxAndFeeItems.some((fee) => fee.title === "Platform Fee")
  const platformFeeAmount = hasPlatformFee ? 0 : Number(tax_and_fee ?? 0)
  if (platformFeeAmount > 0) {
    feeChildren.push({ label: i18n.t("booking.summary.platform_fee"), amount: numberToCurrency(platformFeeAmount) })
  }
  const customFeeTotal = taxAndFeeItems.reduce((sum, fee) => sum + Number(fee.price), 0)
  const taxFeesTotal = customFeeTotal + platformFeeAmount
  if (taxFeesTotal > 0) {
    breakdown.push({
      label: "Tax & Fees",
      amount: numberToCurrency(taxFeesTotal),
      children: feeChildren.length > 0 ? feeChildren : undefined
    })
  }

  // Post-creation manual price adjustment (booking-details "Adjust Booking Price") — carried as a
  // flat line after Tax & Fees so the seeded breakdown reconciles with total_price (which includes it).
  const priceAdjustmentTotal = (paymentSummary?.adjust_price_history ?? []).reduce(
    (sum, item) => sum + Number(item?.price_difference || 0),
    0
  )
  if (priceAdjustmentTotal) {
    breakdown.push({
      label: "Price Change",
      amount: `${priceAdjustmentTotal > 0 ? "+" : ""}${numberToCurrency(priceAdjustmentTotal)}`,
      isPriceChange: true
    })
  }

  return breakdown
}

/**
 * Calculates the total booking amount and breakdown
 * @param {Object} bookingSelections - The booking selections object
 * @param {Object} counts - Guest counts
 * @param {Number} guideFeePercent - Guide fee percentage
 * @param {Object} options - Additional options
 * @param {String} options.paymentType - Payment type (cash, card, link, etc)
 * @param {Boolean} options.isEdit - Whether this is an edit operation
 * @param {Number} options.originalTaxAndFee - Original platform fee from booking.payment_summary.tax_and_fee (cash edit mode only)
 * @param {Array} options.customFees - Custom fees from assigned_custom_fees (e.g. [{ id, title, fee_type: "fixed"|"percentage", price }])
 * @param {Object} options.platformFeeSetting - Platform fee setting ({ fee_payer_mode, split_type?, guide_portion?, card_fee_passthrough_enabled? })
 * @param {Object} options.cardFeeInfo - Card fee info from selected payment method ({ rate, fixed, formula })
 * @param {Number} options.originalCardProcessingFee - Original card processing fee from booking.payment_summary.card_processing_fee (cash edit mode only)
 * @param {Boolean} options.hasCollectedPayment - Edit only: whether money has been taken on the booking (hasCollectedPayment(booking)). Cash-edit keeps the original platform and card fees only when it has; otherwise cash is priced like a new cash booking (neither fee). Defaults to true, the previous behaviour.
 * @param {Boolean} options.isCollectingNow - Edit only: whether the guide is charging a card on this save (the "Collect payment now?" answer). False keeps any already-settled fee and adds no new one, since the increase becomes an outstanding balance. Defaults to true, the previous behaviour.
 * @param {Object} options.priorCardCharge - The card charge already settled ({ fee, preCardTotal }), from getPriorCardCharge(booking). When present, the settled fee is kept and only the price increase is grossed up, because the top-up is a separate Stripe charge carrying its own fixed fee. On a decrease the fee is re-grossed on the new base and fixed fees already paid are kept, matching the backend.
 * @param {Array} options.discountPromos - Effective discount promos ({ value, discount_type, apply_to_add_ons }). The discount is applied to the pre-tax base (guest subtotal + add-ons) BEFORE tax & fees are calculated, matching the backend.
 * @param {Array} options.dayAdjustments - Day adjustments from experience.day_adjustments ({ name, direction, price_value, price_type, applies_on, custom_days, affects }). When the trip's start date (bookingSelections.date) falls on a matching day, these are folded directly into the base unit prices (flat_rate, price_per_person, price_per_child, price_per_infant, price_per_passenger) before any totals are computed.
 * @param {Array} options.seasonalAdjustments - Seasonal adjustments from experience.seasonal_adjustments ({ name, direction, price_value, price_type, applies_on_schedule_type, date_ranges, start_date, end_date, is_repeat_each_year, affects }). Folded into the base unit prices exactly like dayAdjustments when the trip's start date falls inside one of their ranges; the two stack, each computed off the base price.
 * @param {Array} options.seasonRanges - The experience's own season windows (experience.available_date_ranges, [{ start, end }]). Supplies the dates for seasonal adjustments whose applies_on_schedule_type is "default_schedule".
 * @param {Object} options.trip - The experience record, used only for its progressive_pricing_* fields. On a dynamic multi-day booking (a date range, not a single date), each covered date's day/seasonal-adjusted rate is additionally stepped by getProgressiveAdjustment for that date's position in the span — mirroring priceRateAcrossDates in multiDayPricing.js, the guest-widget equivalent. Omitted (or a trip with progressive pricing disabled) leaves per-day pricing exactly as before.
 * @param {Number} options.depositFraction - Fraction of the pre-card total paid by card NOW (deposit% / 100 for deposit payments; 1 for full payment). The card processing fee is charged only on this pay-now portion, not on the full total.
 * @param {Array} options.addOnsCatalog - Experience add-ons catalog (experience.add_ons). Used with the selections in bookingSelections.addOns to decide, per add-on, whether the platform fee (add_on_category.commission_applicable / commission_percentage) and the custom percentage fees (apply_custom_tax_and_fees) apply to it. The card processing fee always applies to every add-on regardless of these flags.
 * @param {Number} options.priceAdjustmentTotal - Cumulative post-creation manual price adjustment (sum of payment_summary.adjust_price_history.price_difference). Folded into the tax & fee base so percentage-based fees apply to the adjusted amount (matching the stored "Previous Price Details"), then added as a flat "Price Change" line after Tax & Fees so the details-page "Adjust Booking Price" change persists through edits. Edit mode only (0 for new bookings).
 * @returns {{ total: number, guestSubtotal: number, discountAmount: number, breakdown: Array, preCardTotal: number, cardProcessingFee: number }}
 *   `total` includes the card fee on the deposit; `preCardTotal` is the total before any card fee
 *   (the base the deposit % is taken from); `cardProcessingFee` is the deposit's card fee.
 */

// The following helpers are pulled out of calculateBookingTotal so their branching doesn't count
// toward that function's own cognitive complexity — each is a pure function of the inputs it's
// given, called once from calculateBookingTotal and merged into its running total/breakdown.

/**
 * Flat-rate pricing: the flat amount plus guest-capacity allocation across adults/children/infants
 * (in that priority order — passengers are always billed as extra), and the resulting extra-guest
 * line items. Contributes nothing when no guests are selected at all (the bare flat rate is never
 * charged on its own).
 */
const calculateFlatRatePricing = ({
  totalCounts,
  adjustedFlatRate,
  adjustedPricePerPerson,
  adjustedPricePerChild,
  adjustedPricePerInfant,
  includedGuests,
  isZeroed,
  flatRate,
  pricePerPerson
}) => {
  const hasGuests = Object.values(totalCounts).some((val) => val > 0)
  if (!hasGuests) return { total: 0, breakdown: [] }

  let total = adjustedFlatRate
  const breakdown = []

  let remainingCapacity = includedGuests

  // 1. Adults
  const adultsIncluded = Math.min(totalCounts.adults, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - adultsIncluded)

  // 2. Children
  const childrenIncluded = Math.min(totalCounts.children, remainingCapacity)
  remainingCapacity = Math.max(0, remainingCapacity - childrenIncluded)

  // 3. Infants
  const infantsIncluded = Math.min(totalCounts.infants, remainingCapacity)

  const extraAdults = Math.max(0, totalCounts.adults - adultsIncluded)
  const extraChildren = Math.max(0, totalCounts.children - childrenIncluded)
  const extraInfants = Math.max(0, totalCounts.infants - infantsIncluded)

  breakdown.push({
    label: "Flat Rate",
    amount: numberToCurrency(adjustedFlatRate),
    rawAmount: adjustedFlatRate,
    // Guests bundled into the flat rate itself — needed so a fixed-$ progressive-pricing
    // delta (one flat amount per guest, see foldProgressiveDeltaIntoBreakdown) is folded
    // in per guest rather than proportionally by dollar share.
    guestCount: adultsIncluded + childrenIncluded + infantsIncluded,
    isZeroedByAdjustment: isZeroed(flatRate),
    pricingDetails: {
      pricing_type: "charge_flat_rate",
      included_guests: includedGuests,
      price_per_person: adjustedPricePerPerson,
      price_per_child: adjustedPricePerChild,
      price_per_infant: adjustedPricePerInfant
    }
  })

  if (extraAdults > 0) {
    const lineTotal = extraAdults * adjustedPricePerPerson
    total += lineTotal
    breakdown.push({
      label: `Extra Adult x ${extraAdults}`,
      amount: numberToCurrency(lineTotal),
      rawAmount: lineTotal,
      guestCount: extraAdults,
      isZeroedByAdjustment: isZeroed(pricePerPerson)
    })
  }
  if (extraChildren > 0) {
    const lineTotal = extraChildren * adjustedPricePerPerson
    total += lineTotal
    breakdown.push({
      label: `Extra Child x ${extraChildren}`,
      amount: numberToCurrency(lineTotal),
      rawAmount: lineTotal,
      guestCount: extraChildren,
      isZeroedByAdjustment: isZeroed(pricePerPerson)
    })
  }
  if (extraInfants > 0) {
    const lineTotal = extraInfants * adjustedPricePerPerson
    total += lineTotal
    breakdown.push({
      label: `Extra Infant x ${extraInfants}`,
      amount: numberToCurrency(lineTotal),
      rawAmount: lineTotal,
      guestCount: extraInfants,
      isZeroedByAdjustment: isZeroed(pricePerPerson)
    })
  }
  if (totalCounts.passengers > 0) {
    const lineTotal = totalCounts.passengers * adjustedPricePerPerson
    total += lineTotal
    breakdown.push({
      label: `Extra Passenger x ${totalCounts.passengers}`,
      amount: numberToCurrency(lineTotal),
      rawAmount: lineTotal,
      guestCount: totalCounts.passengers,
      isZeroedByAdjustment: isZeroed(pricePerPerson)
    })
  }

  return { total, breakdown }
}

/**
 * Per-person pricing: each guest type (adult/child/infant/passenger) billed at its own
 * (day/seasonal-adjusted) unit rate.
 */
const calculatePerPersonPricing = ({
  totalCounts,
  adjustedPricePerPerson,
  adjustedPricePerChild,
  adjustedPricePerInfant,
  adjustedPricePerPassenger,
  pricePerPerson,
  pricePerChild,
  pricePerInfant,
  pricePerPassenger,
  isZeroed
}) => {
  const guestMapping = [
    { key: "adults", label: "Adult", basePrice: pricePerPerson, price: adjustedPricePerPerson },
    { key: "children", label: "Child", basePrice: pricePerChild, price: adjustedPricePerChild },
    { key: "infants", label: "Infant", basePrice: pricePerInfant, price: adjustedPricePerInfant },
    { key: "passengers", label: "Passenger", basePrice: pricePerPassenger, price: adjustedPricePerPassenger }
  ]

  let total = 0
  const breakdown = []
  for (const { key, label, basePrice, price } of guestMapping) {
    const count = totalCounts[key] || 0
    if (count > 0) {
      const unitPrice = price
      const lineTotal = unitPrice * count
      total += lineTotal
      breakdown.push({
        label: `${label} x ${count}`,
        amount: numberToCurrency(lineTotal),
        rawAmount: lineTotal,
        guestCount: count,
        guestType: key,
        isZeroedByAdjustment: isZeroed(basePrice)
      })
    }
  }

  return { total, breakdown }
}

/**
 * Folds the progressive duration-pricing delta directly into the existing guest-type
 * breakdown lines (Adult/Child/.../Flat Rate), split evenly per guest across every line's
 * own `guestCount` — matching how the aggregate delta itself was computed (value * sign *
 * guestCount per ramped day, see getProgressiveAdjustment/progressiveFixedDeltaTotal), so
 * every guest gets the same flat per-guest adjustment regardless of their own guest type's
 * rate. A proportional-by-dollar split (the previous approach here, still mirrored by the
 * backend's TripBooking#distribute_progressive_delta) only agrees with that per-guest
 * invariant when every guest type happens to share the same rate — otherwise it shorts the
 * cheaper guest type and overcharges the pricier one for the exact same number of ramped
 * days. No separate "Progressive Duration Pricing" line — the adjustment reads directly in
 * the guest-type amounts instead.
 *
 * Only ever called for the fixed/$-unit ramp (progressiveFixedDeltaTotal returns 0
 * otherwise) — the percent-unit ramp is already folded per-guest-type further up (each
 * guest type's own rate is scaled directly, which is exact since percent is scale-
 * invariant).
 *
 * @param {Array} breakdown - lines carrying a `rawAmount` and `guestCount` (see
 *   calculateFlatRatePricing / calculatePerPersonPricing)
 * @param {Number} progressiveDelta - the aggregate delta to distribute (already floored by
 *   the caller so the overall total never goes negative)
 */
const foldProgressiveDeltaIntoBreakdown = (breakdown, progressiveDelta) => {
  if (!progressiveDelta) return breakdown

  const totalGuestCount = breakdown.reduce((sum, line) => sum + (line.guestCount || 0), 0)
  if (totalGuestCount <= 0) return breakdown

  let remainder = progressiveDelta
  const updated = breakdown.map((line) => {
    if (!line.guestCount) return line
    const share = roundToCents(progressiveDelta * (line.guestCount / totalGuestCount))
    const newRaw = Math.max(0, roundToCents(line.rawAmount + share))
    remainder = roundToCents(remainder - (newRaw - line.rawAmount))
    return { ...line, rawAmount: newRaw, amount: numberToCurrency(newRaw) }
  })

  // Any cent of rounding slack (or a floor-at-zero clamp eating into one line's share)
  // goes to whichever line is now largest — same remainder-absorption rule the backend's
  // reconcile_line_items! uses, so the two never drift apart over an edge case.
  if (Math.abs(remainder) >= 0.01) {
    const largest = [...updated].sort((a, b) => (b.rawAmount || 0) - (a.rawAmount || 0))[0]
    if (largest) {
      largest.rawAmount = Math.max(0, roundToCents(largest.rawAmount + remainder))
      largest.amount = numberToCurrency(largest.rawAmount)
    }
  }

  return updated
}

/**
 * Applies the discount to the pre-tax base (guest subtotal + add-ons) BEFORE tax & fees are
 * calculated, matching the backend. Each promo's apply_to_add_ons flag decides whether add-ons are
 * part of its discountable base. Also returns the uniform factor used to spread that single
 * discount across every taxable portion (guest base, and separately any add-on portions), since
 * add-on portions can be taxed at different rates.
 */
const calculateDiscount = ({ discountPromos, guestSubtotal, addOnsTotal }) => {
  const rawDiscount = (discountPromos ?? []).reduce((sum, promo) => sum + computeDiscountAmount(promo, guestSubtotal, addOnsTotal), 0)
  const discountAmount = Math.max(0, Math.min(rawDiscount, guestSubtotal + addOnsTotal))
  const grossBase = guestSubtotal + addOnsTotal
  const discountFactor = grossBase > 0 ? (grossBase - discountAmount) / grossBase : 1
  return { discountAmount, discountFactor }
}

/**
 * Custom fees. Percentage fees apply to `customFeeBase` (the guest subtotal plus only the add-ons
 * that opted in via apply_custom_tax_and_fees, already folded in by the caller); fixed fees stay
 * flat (charged once). Excludes system-injected fees (id: null) such as "Platform Fee" and "Card
 * Processing Fee" — those are handled separately via guideFeePercent and cardFeeInfo (see
 * resolvePlatformFee/resolveCardFee) to avoid double-counting.
 *
 * Note on floating-point: this sums every fee into `customFeeTotal` here, and the caller does one
 * `total += customFeeTotal`, whereas the pre-extraction code added each fee to `total` one at a
 * time inside the loop. Both compute the same mathematical sum in the same left-to-right order, so
 * in practice they agree (verified against the original implementation across several multi-fee,
 * fractional-percentage scenarios with byte-identical results) — but floating-point addition isn't
 * strictly associative, so grouping the additions differently is not a *guaranteed* bit-for-bit
 * match in every possible input. Currency values are rounded to cents for display via
 * numberToCurrency, so any divergence would be sub-cent.
 */
const calculateCustomFees = ({ customFees, customFeeBase }) => {
  const userCustomFees = customFees?.filter((fee) => fee.id !== null) ?? []
  const customFeeItems = []
  let customFeeTotal = 0
  for (const fee of userCustomFees) {
    // Rounded to cents here, matching the backend's custom_fees_total (a decimal(10,2)
    // column) — summing full-precision fees and rounding only once at the very end landed
    // a cent away from the backend's own total, since the backend's card-fee gross-up is
    // built from these already-rounded component amounts, not raw floats.
    const feeAmount = roundToCents(
      fee.fee_type === "percentage" ? (customFeeBase * Number(fee.price)) / 100 : Number(fee.price)
    )
    if (feeAmount === 0) continue
    customFeeTotal += feeAmount
    // isLineItem: the guide chose (Taxes & Fees settings) to list this fee on its own line.
    customFeeItems.push({ label: fee.title, amount: numberToCurrency(feeAmount), rawAmount: feeAmount, isLineItem: fee.show_as_line_item === true })
  }
  return { customFeeTotal, customFeeItems }
}

/**
 * Platform fee (commission): recalculated the same way for card AND cash — EXCEPT a brand-new
 * (not-yet-created) full-cash booking. There's no history to preserve there (fixedPlatformFee
 * below requires isEdit), and the backend's TripBooking#ds_client_portion unconditionally zeroes
 * the CLIENT-facing platform fee for that exact case (CASH_BALANCE_PAYMENT_OPTIONS - the guide
 * absorbs the commission from payout instead, since there's no charge to attach it to). Every
 * other cash case - a deposit's cash-balance leg (still paymentType "card" for the deposit) and
 * cash-edit (preserved via fixedPlatformFee) - is unaffected by this and stays in parity with
 * the card calculation. When it does apply, it's the guide fee on the guest subtotal PLUS the
 * commission on any commission-applicable add-ons (each at its own commission_percentage, or the
 * standard guide fee when null); the combined amount then follows platform_fee_setting where it's
 * applied (see the caller).
 */
const resolvePlatformFee = ({ isEdit, isCashPayment, guestSubtotal, discountFactor, guideFeePercent, commissionAddOns, originalTaxAndFee }) => {
  const rawPlatformTax =
    !isEdit && isCashPayment
      ? 0
      : (guestSubtotal * discountFactor * (Number(guideFeePercent) || 0)) / 100 +
        commissionAddOns.reduce((sum, addOn) => {
          const rate = addOn.commissionPercentage != null ? addOn.commissionPercentage : Number(guideFeePercent) || 0
          return sum + (addOn.amount * discountFactor * rate) / 100
        }, 0)
  const fixedPlatformFee = isCashPayment && isEdit && originalTaxAndFee != null ? Number(originalTaxAndFee) : null
  return { rawPlatformTax, fixedPlatformFee }
}

/**
 * Card fee: recalculate for card/link; preserve original for cash-edit; zero otherwise. The card
 * processing fee is only passed on to the guest when the experience's platform_fee_setting opts in
 * via card_fee_passthrough_enabled. PowerTranz charges (powertranzLink/powertranzCard) use the guide's
 * own PowerTranz rate (platform_fee_setting.powertranz_card_fee_rate, no fixed part) instead of
 * Stripe's, same as the guest checkout.
 */
const resolveCardFee = ({ paymentType, isCashPayment, isEdit, customFees, cardFeeInfo, platformFeeSetting, originalCardProcessingFee }) => {
  const isCardOrLinkPayment = ["card", "link", "paypalLink", "powertranzLink", "powertranzCard"].includes(paymentType)
  const isPowerTranzPayment = ["powertranzLink", "powertranzCard"].includes(paymentType)
  const powerTranzCardFee = getPowerTranzCardFee(platformFeeSetting)
  const systemCardFeeEntry = customFees?.find((fee) => fee.id === null && fee.title === "Card Processing Fee")
  const cardFeePassthroughEnabled = platformFeeSetting?.card_fee_passthrough_enabled === true
  const isCardFeeActive = !!systemCardFeeEntry && systemCardFeeEntry.is_active !== false
  const stripeCardFeeSource = cardFeeInfo ?? (isCardFeeActive ? { rate: Number(systemCardFeeEntry?.price) / 100, fixed: 0.3 } : null)
  const cardFeeSource = isPowerTranzPayment ? (powerTranzCardFee.enabled ? powerTranzCardFee : null) : stripeCardFeeSource
  const fixedOriginalCardFee = isCashPayment && isEdit && Number(originalCardProcessingFee) > 0 ? Number(originalCardProcessingFee) : 0
  const shouldApplyCardFee =
    (isCardOrLinkPayment && (isPowerTranzPayment || isCardFeeActive) && !!cardFeeSource && cardFeePassthroughEnabled) ||
    fixedOriginalCardFee > 0
  return { cardFeeSource, fixedOriginalCardFee, shouldApplyCardFee }
}

/**
 * Resolves and applies the "Tax & Fees" breakdown line (custom fees + the platform fee) and, when
 * applicable, the card processing fee — both folded into the running total, but the card fee is
 * returned separately and never listed under Tax & Fees. Same gating as the original inline block, including the `total > 0` gate (a zeroed booking gets no fees at all at all).
 *
 * The card fee is charged on the amount paid by card NOW: the deposit (depositFraction of the
 * pre-card total) for deposit payments, or the whole pre-card total when paying in full
 * (depositFraction = 1). The post-creation price adjustment is included in the fee base whenever
 * the full amount is paid by card (depositFraction = 1: full payment, or deposit + balance-by-card)
 * — otherwise adding a guest could wrongly LOWER the card fee below the stored booking's, since the
 * tax base already includes the adjustment. When only the deposit is swiped now (deposit +
 * balance-in-cash, depositFraction < 1) the adjustment is paid later in cash, so it's excluded.
 * The fee itself is grossed up so that after Stripe deducts its fee on (depositBase + fee), we net
 * depositBase.
 */
const applyTaxAndFees = ({
  total,
  rawPlatformTax,
  fixedPlatformFee,
  customFeeTotal,
  customFeeItems,
  shouldApplyCardFee,
  fixedOriginalCardFee,
  cardFeeSource,
  depositFraction,
  priceAdjustmentTotal,
  platformFeeSetting,
  priorCardCharge,
  isCollectingNow,
  isPlatformFeeLineItem = false
}) => {
  let runningTotal = total
  let cardProcessingFee = 0

  if (!(runningTotal > 0 && (rawPlatformTax > 0 || fixedPlatformFee != null || customFeeItems.length > 0 || shouldApplyCardFee))) {
    return { total: runningTotal, cardProcessingFee, breakdownEntry: null }
  }

  let platformTaxAmount = 0
  if (fixedPlatformFee != null) {
    platformTaxAmount = fixedPlatformFee
    runningTotal += platformTaxAmount
  } else if (rawPlatformTax > 0) {
    // Rounded to cents, matching the backend's ds_commission / platform_fee_client_total
    // (both decimal(10,2) columns) — the card-fee gross-up below is built from this
    // already-rounded figure on the backend, not the raw percentage math.
    platformTaxAmount = roundToCents(applyPlatformFeeSetting(rawPlatformTax, platformFeeSetting))
    runningTotal += platformTaxAmount
  }

  const allChildren = [...customFeeItems]
  if (platformTaxAmount > 0) {
    allChildren.push({
      label: i18n.t("booking.summary.platform_fee"),
      amount: numberToCurrency(platformTaxAmount),
      rawAmount: platformTaxAmount,
      isLineItem: isPlatformFeeLineItem
    })
  }

  if (shouldApplyCardFee) {
    const adjustmentPaidByCard = depositFraction >= 1 ? priceAdjustmentTotal : 0
    const depositBase = runningTotal * depositFraction + adjustmentPaidByCard
    if (!isCollectingNow) {
      // Nothing is being charged on this save, so the quote keeps exactly what was already taken:
      // no new gross-up, and no removal of a fee Stripe has already collected.
      cardProcessingFee = Number(priorCardCharge?.fee) || 0
    } else if (fixedOriginalCardFee > 0) {
      cardProcessingFee = fixedOriginalCardFee
    } else if (priorCardCharge) {
      // A charge has already settled, so raising the price means a second Stripe charge with its
      // own fixed fee. Keep what was taken and gross up only the increase — one gross-up of the
      // combined total would carry a single $0.30 and leave the guide short (see
      // getPriorCardCharge). The price adjustment is new money too, so it joins the increase on
      // the same terms.
      //
      // A decrease is the mirror, matching what the backend saves (TripBooking
      // #calculate_card_processing_fee): one gross-up on the new, lower base plus the fixed fees
      // of any later charges, which it keeps as a cent-rounded stamp. So the percentage part
      // falls with the price and is refunded, while fixed fees already paid stay. That stamp is
      // not in the payload, but it is whatever the settled fee holds beyond a single gross-up of
      // the old base — rounded to cents it comes back exactly.
      const increase = (runningTotal - priorCardCharge.preCardTotal) * depositFraction + adjustmentPaidByCard
      if (Math.abs(increase) < 0.01) {
        // Nothing more is being collected — no price change to the cent and no adjustment on the
        // card — so there is no second Stripe charge and no second fixed fee. The settled charge
        // stands as it is.
        //
        // The threshold is a cent, not zero, because preCardTotal is rebuilt from the booking's
        // cent-rounded saved values while runningTotal is recomputed at full precision. A residue
        // as small as 1.4e-14 used to land in the increase branch below, where grossUpCardFee adds
        // the flat $0.30 regardless of how small the base is — quoting $0.31 of card fee, and a
        // matching phantom "Remaining Balance", on a fully paid booking whose price never moved.
        cardProcessingFee = priorCardCharge.fee
      } else if (increase > 0) {
        cardProcessingFee = roundToCents(priorCardCharge.fee + grossUpCardFee(increase, cardFeeSource))
      } else {
        const previousBase = depositBase - increase
        const extraTransactionFees = roundToCents(priorCardCharge.fee - grossUpCardFee(previousBase, cardFeeSource))
        cardProcessingFee = Math.max(0, roundToCents(grossUpCardFee(depositBase, cardFeeSource) + extraTransactionFees))
      }
    } else {
      // Rounded to cents here, matching the backend's calculate_card_processing_fee, which
      // ends with exactly this: `(fee + extra_card_transaction_fees).round(2)`.
      cardProcessingFee = roundToCents(grossUpCardFee(depositBase, cardFeeSource))
    }
    runningTotal += cardProcessingFee
  }

  // The card processing fee is deliberately NOT part of Tax & Fees. It is a transaction fee on the
  // amount being charged, so it is presented on its own line below the Total — see PriceBreakdown,
  // which reads it from `cardProcessingFee` / `preCardTotal`.
  const hasTaxAndFees = customFeeTotal + platformTaxAmount > 0 || allChildren.length > 0
  return {
    total: runningTotal,
    cardProcessingFee,
    breakdownEntry: hasTaxAndFees
      ? {
          label: "Tax & Fees",
          amount: numberToCurrency(customFeeTotal + platformTaxAmount),
          rawAmount: customFeeTotal + platformTaxAmount,
          isTaxFee: true,
          children: allChildren.length > 0 ? allChildren : undefined
        }
      : null
  }
}

export const calculateBookingTotal = (bookingSelections, counts, guideFeePercent, options = {}) => {
  const {
    paymentType,
    isEdit,
    originalTaxAndFee,
    customFees = [],
    platformFeeSetting,
    cardFeeInfo,
    originalCardProcessingFee,
    priorCardCharge = null,
    hasCollectedPayment = true,
    isCollectingNow = true,
    discountPromos = [],
    dayAdjustments = [],
    seasonalAdjustments = [],
    seasonRanges = [],
    depositFraction = 1,
    overrideGuestSubtotal,
    addOnsCatalog = [],
    trip,
    priceAdjustmentTotal = 0
  } = options
  const {
    pricing_type,
    flat_rate,
    price_per_person,
    price_per_child,
    price_per_infant,
    price_per_passenger,
    included_guests,
    max_guests,
    duration_days,
    rate_basis,
    // Set by the backend on a booking's own trip_schedule: these unit prices are the snapshot the
    // trip was sold at, with the day/seasonal surcharges that applied on the day already baked in.
    price_adjustments_applied
  } = resolveSchedulePrice(bookingSelections?.tripLength?.price, bookingSelections?.date, counts)

  // Fold any matching day adjustments (e.g. weekend surcharges) and seasonal adjustments
  // (e.g. a holiday range) directly into the base unit prices before computing totals, so they
  // apply to the flat rate / per-person rates and to extra-guest per-person rates alike.
  // Both kinds stack: each is computed off the base price and the deltas are summed.
  // Skipped entirely on a pre-adjusted snapshot: the surcharge (and, for a multi-day stay, the
  // span multiplication) is already inside the sold rate, so folding today's rules or the day
  // count on top would charge it twice — and a rule matching every date double-charges every
  // booking priced from one. A new selection replaces trip_schedule with the experience's own
  // (un-baked) prices, which correctly start folding (and spanning) again.
  const applicableAdjustments = price_adjustments_applied
    ? []
    : [
        ...getApplicableDayAdjustments(dayAdjustments, bookingSelections?.date),
        ...getApplicableSeasonalAdjustments(seasonalAdjustments, bookingSelections?.date, seasonRanges)
      ]

  // Three shapes:
  //   single_day            -> one evaluation, off the booking's own date. No summing.
  //   multi_day + dynamic   -> ALWAYS a per-day rate (the whole point of "dynamic" is quoting a
  //                            daily rate over a guest-picked range) — the guest-picked start/end
  //                            range is priced day by day and summed, PLUS each day is additionally
  //                            stepped by progressive duration pricing for its 1-based position.
  //   multi_day + fixed     -> depends on how THIS duration option is actually sold, per its own
  //                            rate_basis: "per_day" sums the known day count from the booked date
  //                            (each day its own adjustments, no progressive — that's dynamic-only);
  //                            "total" (or anything else) is a single flat package price, priced
  //                            once off the start date only, same as a single day. A fixed duration
  //                            can legitimately be sold either way (e.g. "py"'s $200/2-days and
  //                            $100/3-days are both rate_basis "total" — flat packages, not $200 or
  //                            $100 PER DAY) — treating every fixed duration as per-day regardless
  //                            of rate_basis overcharges every "total" one by summing days it was
  //                            never priced to scale with.
  //
  // Skipped entirely on a pre-adjusted snapshot: the surcharge (and the span summing) is already
  // inside the sold rate, so folding today's rules or the day count on top would charge it twice —
  // and a rule matching every date double-charges every booking priced from one. A new selection
  // replaces trip_schedule with the experience's own (un-baked) prices, which correctly start
  // folding (and spanning) again.
  const isMultiDay = trip?.duration_type === "multi_day"
  const isDynamic = isMultiDay && trip?.duration_mode === "dynamic"
  const isFixedPerDay = isMultiDay && !isDynamic && rate_basis === "per_day"
  // As getOptionDays (multiDayPricing.js, which imports this file): never trip_duration, a length in minutes.
  const fixedSpanDays = Number(duration_days) || Number(trip?.duration_value) || 0
  const spanDates = bookingSelections?.endDate
    ? enumerateDates(bookingSelections?.date, bookingSelections?.endDate)
    : isFixedPerDay && fixedSpanDays > 1 && bookingSelections?.date
      ? enumerateDates(bookingSelections.date, dayjs(bookingSelections.date).add(fixedSpanDays - 1, "day").format("YYYY-MM-DD"))
      : []
  const isPerDay = !price_adjustments_applied && spanDates.length > 1

  // Day/seasonal adjustments are re-evaluated per individual calendar day across a multi-day span
  // — each covered date gets its own weekday/season match, not the whole stay inheriting a single
  // evaluation from the start date. NOTE: this is a deliberate product choice, not what the
  // backend actually charges — TripBookings::PriceAdjustmentCalculator is only ever constructed
  // with the booking's single booked_date, so on a range that crosses a weekend/weekday or season
  // boundary, this preview WILL diverge from the real charge. Chosen anyway because per-day
  // variation reads more intuitively on the calendar; see the conversation this was decided in for
  // the exact tradeoff.
  //
  // Progressive duration pricing is the one genuinely per-day-index component (dynamic only) —
  // each day's own ramp step, summed separately and added to the same base.
  const adjustPrice = (rawPrice) => {
    if (price_adjustments_applied) return rawPrice
    if (!isPerDay) return applyPriceAdjustments(rawPrice, applicableAdjustments)

    // The schedule's unit rates arrive as decimal strings from the API (e.g. "200.0"), which
    // applyPriceAdjustments parses internally but the arithmetic below does not — `price +
    // daySeasonalDelta` string-concatenates instead of adding whenever `price` is still a string,
    // and the result only reads back as a clean number by accident (no negative delta ever lands
    // in the middle of the string). A negative one — a day/seasonal decrease, or this ramp's own
    // "decrease" direction — produces an unparseable string like "200.0200-20", which Math.max
    // coerces to NaN. Once `total` is NaN, the pricing effect's `total !== currentAmount` guard
    // (NaN !== NaN) never stops being true, so every render keeps calling updateBookingSelections
    // again — a genuine infinite render loop, not just a slow one. Parsing once up front keeps
    // every operation below on numbers.
    const price = Number.parseFloat(rawPrice ?? 0) || 0

    return spanDates.reduce((sum, isoDate, index) => {
      const daySeasonalDelta =
        applyPriceAdjustments(price, [
          ...getApplicableDayAdjustments(dayAdjustments, isoDate),
          ...getApplicableSeasonalAdjustments(seasonalAdjustments, isoDate, seasonRanges)
        ]) - price
      let progressiveDelta = 0
      if (isDynamic) {
        // Only the percent-unit multiplier is embedded here — it's scale-invariant, so it's still
        // correct once this per-unit rate is later multiplied by its own guest type's count. A
        // fixed-unit ramp's flatDelta is deliberately NOT embedded here (getProgressiveAdjustment
        // always returns multiplier=1 for fixed-unit, so this contributes 0 for that case) — see
        // progressiveFixedDeltaTotal below for why, and where it's added instead.
        const { multiplier } = getProgressiveAdjustment(trip, index + 1)
        progressiveDelta = Math.max(price * (multiplier - 1), -price)
      }
      return sum + Math.max(0, price + daySeasonalDelta + progressiveDelta)
    }, 0)
  }

  // A fixed-unit ($) progressive ramp is ONE flat delta per ramped day for the WHOLE per-day
  // charge (every guest combined) — not a per-guest-type amount. Embedding it into each guest
  // type's own per-unit rate (the way the percent-unit multiplier above safely can) would have it
  // multiplied AGAIN by that guest type's count downstream, and a SECOND time for every other
  // guest type with a nonzero count too — overcharging by a factor of the total guest count.
  // Matches the backend: ProgressiveDurationPricingCalculator#delta_for_day's `per_day_rate` is
  // shared_helpers.rb's `amount / days`, where `amount` is the whole per-day-rate booking's total
  // for the actual guest counts, never a single guest type's rate.
  const progressiveFixedDeltaTotal = (totalCounts) => {
    if (!isPerDay || !isDynamic || price_adjustments_applied) return 0
    if (trip?.progressive_pricing_unit !== "fixed") return 0
    const aggregatePerDayRate =
      pricing_type === "charge_flat_rate"
        ? Number(flat_rate) || 0
        : (totalCounts.adults || 0) * (Number(price_per_person) || 0) +
          (totalCounts.children || 0) * (Number(price_per_child) || 0) +
          (totalCounts.infants || 0) * (Number(price_per_infant) || 0) +
          (totalCounts.passengers || 0) * (Number(price_per_passenger) || 0)
    if (!aggregatePerDayRate) return 0
    const guestCount = (totalCounts.adults || 0) + (totalCounts.children || 0) + (totalCounts.infants || 0) + (totalCounts.passengers || 0)
    return spanDates.reduce((sum, _isoDate, index) => {
      const { flatDelta } = getProgressiveAdjustment(trip, index + 1, guestCount)
      if (!flatDelta) return sum
      return sum + Math.max(flatDelta, -aggregatePerDayRate)
    }, 0)
  }
  // Flags the lines whose $0.00 is the clamp rather than the real total, so the breakdown can
  // explain it instead of silently showing a free trip.
  const isZeroed = (price) => isPriceZeroedByAdjustments(price, applicableAdjustments)

  const adjustedFlatRate = adjustPrice(flat_rate)
  const adjustedPricePerPerson = adjustPrice(price_per_person)
  const adjustedPricePerChild = adjustPrice(price_per_child)
  const adjustedPricePerInfant = adjustPrice(price_per_infant)
  const adjustedPricePerPassenger = adjustPrice(price_per_passenger)

  const mainCounts = counts?.main || {}
  const extraCounts = counts?.extra || {}

  // Merge counts for calculation if pricing is flat rate, assuming all inputs flow into main or separate inputs need merging
  // If we unify inputs in GuestInputGroup, mainCounts will hold everything.
  // But we handle both for safety.
  const totalCounts = {
    adults: (mainCounts.adults || 0) + (extraCounts.adults || 0),
    children: (mainCounts.children || 0) + (extraCounts.children || 0),
    infants: (mainCounts.infants || 0) + (extraCounts.infants || 0),
    passengers: (mainCounts.passengers || 0) + (extraCounts.passengers || 0)
  }

  const pricing =
    pricing_type === "charge_flat_rate"
      ? calculateFlatRatePricing({
          totalCounts,
          adjustedFlatRate,
          adjustedPricePerPerson,
          adjustedPricePerChild,
          adjustedPricePerInfant,
          includedGuests: included_guests ?? max_guests ?? 0,
          isZeroed,
          flatRate: flat_rate,
          pricePerPerson: price_per_person
        })
      : calculatePerPersonPricing({
          totalCounts,
          adjustedPricePerPerson,
          adjustedPricePerChild,
          adjustedPricePerInfant,
          adjustedPricePerPassenger,
          pricePerPerson: price_per_person,
          pricePerChild: price_per_child,
          pricePerInfant: price_per_infant,
          pricePerPassenger: price_per_passenger,
          isZeroed
        })

  let total = pricing.total
  let breakdown = [...pricing.breakdown]

  const fixedProgressiveTotal = progressiveFixedDeltaTotal(totalCounts)
  if (fixedProgressiveTotal) {
    total = Math.max(0, total + fixedProgressiveTotal)
    breakdown = foldProgressiveDeltaIntoBreakdown(breakdown, fixedProgressiveTotal)
  }

  // Capture the pricing-loop result before any override so callers can display the original.
  const calculatedGuestSubtotal = total

  if (overrideGuestSubtotal != null && overrideGuestSubtotal >= 0) {
    total = overrideGuestSubtotal
  }

  // Save guest subtotal before adding fees (used as base for percentage-based fees)
  const guestSubtotal = total

  // Add-on prices are always part of the customer total; only the FEES on them are gated per
  // add-on (platform fee via commission_applicable/percentage, custom % fees via
  // apply_custom_tax_and_fees). The card processing fee always applies to every add-on.
  const addOnsTotal = Number(bookingSelections?.addOnsTotal) || 0
  const { customTaxableAddOnsTotal, commissionAddOns } = summarizeAddOnFees(bookingSelections?.addOns, addOnsCatalog)
  total += addOnsTotal

  // Apply the discount to the pre-tax base BEFORE computing tax & fees, matching the backend.
  // Each promo's apply_to_add_ons flag decides whether add-ons are part of its discountable base.
  // Also derives the uniform factor used to spread that discount across every taxable portion —
  // see calculateDiscount's JSDoc.
  const { discountAmount, discountFactor } = calculateDiscount({ discountPromos, guestSubtotal, addOnsTotal })
  total -= discountAmount

  // The post-creation manual price adjustment (details-page "Adjust Booking Price") widens the
  // taxable guest base so percentage-based fees apply to the adjusted amount — matching the stored
  // "Previous Price Details". It's added undiscounted here, and also carried as its own "Price
  // Change" line after Tax & Fees (below). Edit mode only (0 for new bookings).
  const adjustedGuestBase = guestSubtotal * discountFactor + priceAdjustmentTotal

  // Custom fees — see calculateCustomFees's JSDoc for what's included/excluded.
  const customFeeBase = adjustedGuestBase + customTaxableAddOnsTotal * discountFactor
  const { customFeeTotal, customFeeItems } = calculateCustomFees({ customFees, customFeeBase })
  total += customFeeTotal

  // Card processing fee only applies for card/link/paypalLink (PowerTranz: see resolveCardFee) — cash has no card to charge a fee
  // to. The platform (DS commission) fee is NOT payment-method-dependent — see resolvePlatformFee's
  // JSDoc for the full rationale (PlatformFeeCalculator parity, the not-yet-created full-cash
  // exception, etc).
  const isCashPayment = paymentType === "cash"
  // Cash-edit keeps the fees the booking was charged, since a fee once taken is never removed. With
  // nothing ever collected (an expired request) there is no charge to keep them from, so cash is
  // priced like a new cash booking: no platform fee and no card fee, as the backend saves it.
  const keepsOriginalFees = isEdit && hasCollectedPayment
  const { rawPlatformTax, fixedPlatformFee } = resolvePlatformFee({
    isEdit: keepsOriginalFees,
    isCashPayment,
    guestSubtotal,
    discountFactor,
    guideFeePercent,
    commissionAddOns,
    originalTaxAndFee
  })
  const { cardFeeSource, fixedOriginalCardFee, shouldApplyCardFee } = resolveCardFee({
    paymentType,
    isCashPayment,
    isEdit: keepsOriginalFees,
    customFees,
    cardFeeInfo,
    platformFeeSetting,
    originalCardProcessingFee
  })

  // Assembles the "Tax & Fees" breakdown line and folds the platform/card fees into the running
  // total — see applyTaxAndFees's JSDoc for the total>0 gate and the card-fee gross-up rationale.
  const feesResult = applyTaxAndFees({
    total,
    rawPlatformTax,
    fixedPlatformFee,
    customFeeTotal,
    customFeeItems,
    // A fee already taken must still be reported even when nothing is being collected now and the
    // passthrough has since been turned off — otherwise it silently disappears from the total and
    // reads as a refund the guide never owed.
    shouldApplyCardFee: shouldApplyCardFee || (!isCollectingNow && Number(priorCardCharge?.fee) > 0),
    fixedOriginalCardFee,
    priorCardCharge,
    cardFeeSource,
    depositFraction,
    priceAdjustmentTotal,
    platformFeeSetting,
    isCollectingNow,
    isPlatformFeeLineItem: customFees?.find((fee) => fee.id === null && fee.title === "Platform Fee")?.show_as_line_item === true
  })
  total = feesResult.total
  const cardProcessingFee = feesResult.cardProcessingFee
  if (feesResult.breakdownEntry) breakdown.push(feesResult.breakdownEntry)

  // preCardTotal is the grand total before the card fee — the base the deposit % is taken from.
  // Captured before the manual price adjustment so the DEPOSIT base stays unaffected by it (the
  // adjustment flows into the balance, not the deposit). The card fee itself does cover the
  // adjustment when the full amount is paid by card (see the card-fee block above).
  const preCardTotal = total - cardProcessingFee

  // Add the post-creation manual price adjustment (booking-details "Adjust Booking Price") as its
  // own "Price Change" line — the raw base delta. Tax & fees on the adjusted amount were already
  // folded into the fee base above (via adjustedGuestBase), so this line only carries the base change,
  // mirroring the stored "Previous Price Details" layout (taxed adjusted amount + separate change).
  if (priceAdjustmentTotal) {
    total += priceAdjustmentTotal
    breakdown.push({
      label: "Price Change",
      amount: `${priceAdjustmentTotal > 0 ? "+" : ""}${numberToCurrency(priceAdjustmentTotal)}`,
      isPriceChange: true
    })
  }

  return { total, guestSubtotal, calculatedGuestSubtotal, discountAmount, breakdown, preCardTotal, cardProcessingFee }
}

const UNPRICED = { total: 0, guestSubtotal: 0, calculatedGuestSubtotal: 0, breakdown: [], preCardTotal: 0, cardProcessingFee: 0 }

/**
 * calculateBookingTotal (zeros when it can't price the booking), plus preCardTotalBeforeAdjustments:
 * the same booking without the date's day/seasonal adjustments or the multi-day ramp, which New
 * Booking's summaries strike beside the total when the date makes the booking cheaper. `options` is
 * calculateBookingTotal's, with `trip` the experience.
 */
export const priceWithBeforeAdjustments = (selections, counts, guideFeePercent, options) => {
  const price = calculateBookingTotal(selections, counts, guideFeePercent, options) || UNPRICED
  const { preCardTotal: preCardTotalBeforeAdjustments } =
    calculateBookingTotal(selections, counts, guideFeePercent, {
      ...options,
      dayAdjustments: [],
      seasonalAdjustments: [],
      trip: options.trip && { ...options.trip, progressive_pricing_enabled: false }
    }) || {}
  return { ...price, preCardTotalBeforeAdjustments }
}
